import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Logo geometry (brand manual §10, §18.3–18.5).
 *
 * Pure TS, no React and no asset imports, so the Astro marketing site can
 * consume the same numbers as the two React portals — see
 * `apps/website/src/components/brand/Logo.astro`. Asset resolution differs per
 * framework (`?url` in Astro, the bundler's URL loader in Vite), the geometry
 * does not.
 *
 * WHY THIS FILE EXISTS. Before Phase 5 there was no logo component at all: each
 * surface hand-rolled an `<img>`, and five auth/onboarding screens didn't use the
 * logo at all — they drew a green circle containing a single bold capital, the
 * initial of the retired product name. It survived every rename sweep because one
 * letter matches no word. §18.3 wants the marchio and the logotype together, or
 * the marchio alone; a letterform is neither.
 *
 * ── Measured constants ───────────────────────────────────────────────────────
 * All figures below were measured off `pyetro_logo.svg` with `getBBox()` rather
 * than eyeballed, because §18.5's clear space is defined against a glyph width
 * that no amount of reading the file will tell you.
 *
 *   lockup viewBox   660 475 600 130
 *   lockup ink       x 678.8 y 488.9 w 566.2 h 104.4
 *   marchio ink      x 678.8 y 506.1 w 174.2 h  87.2   (paths 6–9)
 *   logotype glyphs  p 66.8 · y 70.0 · e 62.9 · t 42.5 · r 39.1 · o 65.4
 */

/** The lockup's viewBox height, the unit all other figures are relative to. */
const LOCKUP_VIEWBOX_HEIGHT = 130;

/**
 * Width of the "t" in "pyetro" — §18.5's *area di rispetto*: "area di rispetto
 * pari alla larghezza della 't'".
 */
const T_WIDTH = 42.5;

/**
 * Clear space the asset already supplies inside its own viewBox, per side.
 * Padding at the call site only has to make up the difference; ignoring this
 * over-pads by ~15% of the logo height, which pushes the mark off small headers
 * for no reason.
 */
const LOCKUP_BUILT_IN = { left: 18.8, right: 15.0, top: 13.9, bottom: 11.7 };

/** Rendered width ÷ rendered height, for deriving one from the other. */
export const LOGO_ASPECT = 600 / LOCKUP_VIEWBOX_HEIGHT;
export const MARCHIO_ASPECT = 174.2 / 87.2;

/**
 * Clear space to apply *around the rendered box*, as a fraction of rendered
 * height. One uniform number per variant rather than four, because asymmetric
 * padding on a logo is the kind of detail that gets "tidied up" by the next
 * person; the value is the largest of the four shortfalls, so every side ends up
 * at or above the required 42.5 units.
 *
 *   lockup  — worst side is the bottom (42.5 − 11.7 = 30.8 units) → 0.237
 *   marchio — viewBox is tight to the ink, so the full 42.5 units are owed, and
 *             the marchio is only 87.2 units tall → 0.488
 */
export const LOGO_CLEAR_SPACE_RATIO = {
  lockup:
    (T_WIDTH - Math.min(...Object.values(LOCKUP_BUILT_IN))) /
    LOCKUP_VIEWBOX_HEIGHT,
  marchio: T_WIDTH / 87.2,
} as const;

export type LogoVariant = keyof typeof LOGO_CLEAR_SPACE_RATIO;

/**
 * Rendered size + the padding that satisfies §18.5, for a given target height.
 *
 * Rounds the padding up: rounding down would put a logo one sub-pixel inside the
 * rule and make the check un-provable.
 */
export const logoBox = (variant: LogoVariant, height: number) => ({
  width: Math.round(
    height * (variant === 'marchio' ? MARCHIO_ASPECT : LOGO_ASPECT),
  ),
  height,
  clearSpace: Math.ceil(height * LOGO_CLEAR_SPACE_RATIO[variant]),
});

/**
 * The wrapper. It carries only layout — the clear space is an inline style
 * because it is computed from the height, and Tailwind cannot express "padding
 * equal to 23.7% of an arbitrary prop".
 */
export const logoVariants = cva('inline-flex items-center shrink-0', {
  variants: {
    /**
     * `light` is the two-tone lockup: marchio in verde pyetro + petrolio,
     * logotype in petrolio. Correct on white, bruno and grigio.
     *
     * `green` is §10's sanctioned on-petrolio colourway (logotipo in verde
     * pyetro). Needed because the default lockup is petrolio ink, which is
     * invisible on a petrolio band — the footer's original lockup vanished.
     */
    colourway: {
      light: '',
      green: '',
    },
    interactive: {
      true: 'cursor-pointer transition-opacity hover:opacity-80',
      false: '',
    },
  },
  defaultVariants: { colourway: 'light', interactive: false },
});

export type LogoVariantProps = VariantProps<typeof logoVariants>;
